<script setup lang="ts">
import LeTooltip from "@dsk/components/LeTooltip.vue";
import { isEqual } from "lodash-es";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import LeAboutDictionaryButton from "@/components/articles/LeAboutDictionaryButton.vue";
import LeArticleFeedbackButton from "@/components/articles/LeArticleFeedbackButton.vue";
import LeDictionaryCover from "@/components/dictionaries/LeDictionaryCover.vue";
import { DictsysArticleHeaderHelper } from "@/helpers";
import { useArticleFeedbackModalStore, useDictionariesStore, useDictsysInflectionSchemeStore } from "@/stores";
import type { Dictionary, DictsysArticle, DictsysLemma } from "@/types";

import LeArticleGenders from "./LeArticleGenders.vue";
import LeArticleInflections from "./LeArticleInflections.vue";
import LeArticleInflectionSchemeButton from "./LeArticleInflectionSchemeButton.vue";
import LeArticleInlineInflectionSchemeButton from "./LeArticleInlineInflectionSchemeButton.vue";
import LeArticlePronunciationButton from "./LeArticlePronunciationButton.vue";
import LeArticleWordclass from "./LeArticleWordclass.vue";

const props = defineProps<{
  article: DictsysArticle;
  forceInlineButtons?: boolean;
  withDictionaryCover?: boolean;
}>();

const { t } = useI18n();

const dictsysInflectionSchemeStore = useDictsysInflectionSchemeStore();
const { canShowInflectionScheme } = dictsysInflectionSchemeStore;
const dictionariesStore = useDictionariesStore();
const { openFeedbackModal } = useArticleFeedbackModalStore();

const language = computed<"da" | "de">(() => (props.article.usageLanguage === "de" ? "de" : "da"));
const orText = computed<string>(() => (language.value === "de" ? "oder" : "eller"));
const trademarkTooltipText = computed<string>(() => (language.value === "da" ? "varemærke" : "Marke"));

const dictionary = computed<Dictionary | undefined>(() =>
  dictionariesStore.getDictionaryByMethodId(props.article.methodId)
);

const lemmas = computed<DictsysLemma[]>(() => [props.article.article, ...props.article.article.alternativeSpellings]);

const headerLemmas = computed<DictsysArticleHeaderHelper.HeaderLemma[]>(() =>
  DictsysArticleHeaderHelper.getHeaderLemmas(headerCommonInfo.value, lemmas.value, language.value)
);
const headerCommonInfo = computed<DictsysArticleHeaderHelper.HeaderCommonInfo>(() =>
  DictsysArticleHeaderHelper.getHeaderCommonInfo(lemmas.value, language.value)
);

const lemmasWithInflectionScheme = computed<DictsysLemma[]>(() =>
  lemmas.value.filter((lemma) =>
    canShowInflectionScheme(props.article.sourceLanguage, lemma.wordclass, lemma.inflectionScheme)
  )
);

const lemmaIdsWithInlineInflections = computed<string[]>(() => {
  const hasDifferentInflectionsThanFirstLemma = (lemma: DictsysLemma) =>
    !isEqual(
      props.article.article.inflections.byForm.map((f) => f.short),
      lemma.inflections.byForm.map((f) => f.short)
    );

  return lemmas.value
    .filter((lemma) => lemma.inflections.byForm)
    .filter((lemma, index) => index === 0 || hasDifferentInflectionsThanFirstLemma(lemma))
    .map((lemma) => lemma.id);
});

const inflectionSchemeButtonType = computed<"none" | "inline" | "button">(() => {
  if (lemmasWithInflectionScheme.value.length === 0) {
    return "none";
  }

  if (props.forceInlineButtons) {
    return "inline";
  }

  if (lemmasWithInflectionScheme.value.length === 1) {
    return "button";
  }

  return "inline";
});

const onOpenFeedbackModal = () => {
  if (!dictionary.value) {
    return;
  }

  openFeedbackModal(props.article, dictionary.value, props.article.methodId);
};
</script>

<template>
  <div
    class="le-article-header"
    :class="{
      'le-article-header--thin-padding': !headerCommonInfo.isEmpty() || headerLemmas.length > 1,
      'le-article-header--with-dictionary-cover': dictionary && props.withDictionaryCover
    }"
  >
    <LeDictionaryCover
      v-if="dictionary && props.withDictionaryCover"
      :dictionary="dictionary"
      size="large"
      with-tooltip
      with-subtitle
    />
    <div class="le-article-header__container">
      <div
        v-for="(lemma, lemmaIndex) in headerLemmas"
        :key="lemma.lemma.id"
        class="le-article-header__row"
        :class="{
          'le-article-header__row--last': lemmaIndex === headerLemmas.length - 1 && headerCommonInfo.isEmpty()
        }"
      >
        <span>
          <span
            v-if="lemma.type === 'alternative'"
            class="le-article-header__separator le-article-header__space-sm"
            :lang="props.article.usageLanguage"
          >
            {{ orText }}
          </span>

          <span class="le-article-header__head-word" :lang="props.article.sourceLanguage">
            {{ lemma.lemma.headWord
            }}<LeTooltip v-if="lemma.lemma.metadata.trademark" :text="trademarkTooltipText" :margin-bottom="8">
              <span>®</span>
            </LeTooltip>
          </span>

          <template v-for="(info, infoKey) in lemma.info.getFiltered()" :key="infoKey">
            <span
              class="le-article-header__common-info le-article-header__space-md"
              :class="{ 'le-article-header__space-sm': infoKey === lemma.info.getFiltered().length - 1 }"
              :lang="props.article.usageLanguage"
            >
              ({{ info.join(", ") }})
            </span>
          </template>

          <LeArticleWordclass
            :wordclass="lemma.lemma.wordclass"
            :usage-language="props.article.usageLanguage"
            :lang="props.article.usageLanguage"
            class="le-article-header__space-sm"
          />

          <LeArticleGenders
            :genders="lemma.lemma.metadata.genders"
            :display-language="props.article.sourceLanguage"
            :usage-language="props.article.usageLanguage"
            :lang="props.article.usageLanguage"
            class="le-article-header__space-sm"
          />

          <LeArticleInflections
            v-if="lemmaIdsWithInlineInflections.includes(lemma.lemma.id)"
            :inflections="lemma.lemma.inflections"
            :usage-language="props.article.usageLanguage"
            :target-language="props.article.targetLanguage"
            class="le-article-header__space-md"
          />

          <LeArticleInlineInflectionSchemeButton
            v-if="inflectionSchemeButtonType === 'inline'"
            :language="props.article.sourceLanguage"
            :head-word="lemma.lemma.headWord"
            :wordclass="lemma.lemma.wordclass"
            :inflection-scheme="lemma.lemma.inflectionScheme"
            :scheme-id="props.article.id"
            :usage-language="props.article.usageLanguage"
          />

          <LeArticlePronunciationButton
            v-if="lemma.lemma.pronunciationFileUrl"
            class="le-article-header__pronunciation-button"
            :pronunciation-file-url="lemma.lemma.pronunciationFileUrl"
            :usage-language="props.article.usageLanguage"
            type="headword"
          />

          <LeArticleFeedbackButton
            v-if="lemmaIndex === 0"
            class="le-article-header__article-feedback-button le-article-header__article-feedback-button--mobile"
            :tooltip="t('le-dictionary-feedback.button-tooltip.text')"
            @click="onOpenFeedbackModal()"
          />
        </span>
      </div>

      <div v-if="!headerCommonInfo.isEmpty()" class="le-article-header__common-info-container">
        <span class="le-article-header__common-info" :lang="props.article.usageLanguage">
          {{ headerCommonInfo.getFilteredFlattened().join(", ") }}
        </span>
      </div>
    </div>

    <LeArticleFeedbackButton
      class="le-article-header__article-feedback-button"
      :tooltip="t('le-dictionary-feedback.button-tooltip.text')"
      @click="onOpenFeedbackModal()"
    />

    <LeArticleInflectionSchemeButton
      v-if="inflectionSchemeButtonType === 'button'"
      :language="props.article.sourceLanguage"
      :head-word="lemmasWithInflectionScheme[0].headWord"
      :wordclass="lemmasWithInflectionScheme[0].wordclass"
      :inflection-scheme="lemmasWithInflectionScheme[0].inflectionScheme"
      :scheme-id="props.article.id"
    />

    <LeAboutDictionaryButton
      v-if="dictionary && props.withDictionaryCover"
      :dictionary="dictionary"
      :tooltip="t('le-dictionary-info-button.tooltip')"
    />
  </div>
</template>

<style lang="less" scoped>
@import "@dsk/styles/all";
@import "@/assets/styles/dictsys-article";

.le-article-header {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 30px;

  #media-breakpoint-up(md, {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding-top: 24px;
    padding-bottom: 24px;
  });

  &--thin-padding {
    padding-top: 16px;
    padding-bottom: 16px;
  }

  &--with-dictionary-cover {
    flex-direction: row;
    padding: 10px 16px;

    #media-breakpoint-up(md, {
      justify-content: flex-start;
    });
  }

  &__container {
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 100%;
  }

  &__row {
    #media-breakpoint-up(md, {
      margin-bottom: 5px;
    });

    &--last {
      margin-bottom: 0;
    }
  }

  &__space-sm {
    margin-right: 8px;
  }

  &__space-md {
    margin-right: 10px;
  }

  &__head-word {
    display: inline-block; // Avoid double-click to mark issue
    margin-right: 16px;
    color: @colors[@primary][700];
    word-break: break-word;
    hyphens: auto;
    .dictsys-article-header();
  }

  &__pronunciation-button {
    top: 2px;
    margin-left: 8px;
  }

  &__common-info-container {
    margin-top: 5px;
  }

  &__separator,
  &__common-info {
    color: @colors[@neutrals][300];
    .dictsys-article-text();
  }

  &__common-info {
    font-style: italic;
  }

  &__article-feedback-button {
    display: none;

    #media-breakpoint-up(md, {
      display: block;
    });

    &--mobile {
      display: block;
      float: right;

      #media-breakpoint-up(md, {
        display: none;
      });
    }
  }
}
</style>
